import { Link } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import { collection, getDocs, limit, orderBy, query } from "firebase/firestore";
import { db } from "../../Firebase";
import "../../Admin.css";
import AdminNav from "../../components/AdminNav";

function fmtDate(v) {
  if (!v) return "";
  // Firestore Timestamp
  if (typeof v?.toDate === "function") {
    const d = v.toDate();
    return d.toLocaleDateString();
  }
  // ISO string
  if (typeof v === "string") {
    const d = new Date(v);
    if (!isNaN(d.getTime())) return d.toLocaleDateString();
  }
  return "";
}

function money(n, currency = "USD") {
  const num = Number(n);
  if (!isFinite(num)) return "";
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency,
      maximumFractionDigits: 0,
    }).format(num);
  } catch {
    return `$${Math.round(num).toLocaleString()}`;
  }
}

function statusClass(status) {
  const s = String(status || "draft").toLowerCase();
  if (s === "accepted") return "pill ok";
  if (s === "sent" || s === "viewed" || s === "ready") return "pill info";
  if (s === "declined" || s === "void") return "pill bad";
  if (s === "expired") return "pill warn";
  return "pill";
}

export default function AdminEstimatesList() {
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [rows, setRows] = useState([]);
  const [qText, setQText] = useState("");

  useEffect(() => {
    document.title = "Estimates | Admin";
  }, []);

  useEffect(() => {
    let alive = true;

    async function load() {
      setLoading(true);
      setErr("");

      try {
        // Try updatedAt sort first
        const col = collection(db, "publicEstimates");
        let snap;

        try {
          const q1 = query(col, orderBy("updatedAt", "desc"), limit(200));
          snap = await getDocs(q1);
        } catch (e) {
          // Fallback if some docs are missing updatedAt or index isn't ready
          const q2 = query(col, orderBy("createdAt", "desc"), limit(200));
          snap = await getDocs(q2);
        }

        const data = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        if (!alive) return;

        setRows(data);
      } catch (e) {
        if (!alive) return;
        setErr(e?.message || "Failed to load estimates.");
      } finally {
        if (alive) setLoading(false);
      }
    }

    load();
    return () => {
      alive = false;
    };
  }, []);

  const filtered = useMemo(() => {
    const t = qText.trim().toLowerCase();
    if (!t) return rows;

    return rows.filter((r) => {
      const estimateNumber = String(r.estimateNumber || "").toLowerCase();
      const property = String(r.site?.propertyName || "").toLowerCase();
      const addr = String(r.site?.siteAddress?.line1 || "").toLowerCase();
      const customer =
        String(r.customer?.companyName || r.customer?.name || "").toLowerCase();

      return (
        estimateNumber.includes(t) ||
        property.includes(t) ||
        addr.includes(t) ||
        customer.includes(t) ||
        String(r.id).toLowerCase().includes(t)
      );
    });
  }, [rows, qText]);

  return (
    <div className="aePage">
      <AdminNav />
      <div className="aeTop">
        <div>
          <h1>Estimates</h1>
          <p className="aeSub">
            Manage estimate documents and open public views for printing.
          </p>
        </div>

        <div className="aeActions">
          <Link className="btn btnPrimary" to="/admin/estimates/new">
            + New Estimate
          </Link>
        </div>
      </div>

      <div className="aeBar">
        <input
          className="aeSearch"
          placeholder="Search: estimate #, property, customer, address, blob ID…"
          value={qText}
          onChange={(e) => setQText(e.target.value)}
        />
        <div className="aeCount">
          {loading ? "Loading…" : `${filtered.length} shown`}
        </div>
      </div>

      {err ? <div className="aeError">{err}</div> : null}

      {loading ? (
        <div className="aeSkeleton">
          <div className="skRow" />
          <div className="skRow" />
          <div className="skRow" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="aeEmpty">
          <h3>No estimates yet</h3>
          <p>Create your first estimate document to start sharing print-friendly links.</p>
          <Link className="btn btnPrimary" to="/admin/estimates/new">
            + New Estimate
          </Link>
        </div>
      ) : (
        <div className="aeTableWrap">
          <table className="aeTable">
            <thead>
              <tr>
                <th>Estimate</th>
                <th>Customer</th>
                <th>Property</th>
                <th>Updated</th>
                <th>Total</th>
                <th>Status</th>
                <th className="right">Actions</th>
              </tr>
            </thead>

            <tbody>
              {filtered.map((r) => {
                const cust =
                  r.customer?.companyName ||
                  r.customer?.name ||
                  "—";

                const prop = r.site?.propertyName || "—";
                const updated = fmtDate(r.updatedAt) || fmtDate(r.createdAt) || "—";

                // If you store totals per option, you can decide a default:
                // Here we show accepted option total if accepted; otherwise Option A if present.
                let total = "";
                const currency = r.currency || "USD";

                if (r.status === "accepted" && r.acceptance?.selectedOptionId) {
                  const opt = (r.options || []).find(
                    (o) => o.optionId === r.acceptance.selectedOptionId
                  );
                  total = money(opt?.totals?.grandTotal, currency);
                } else {
                  const optA = (r.options || []).find((o) => o.optionId === "A");
                  total = money(optA?.totals?.grandTotal, currency);
                }

                return (
                  <tr key={r.id}>
                    <td className="mono">
                      <div className="aeMain">
                        <div className="aeTitle">
                          {r.estimateNumber || r.id}
                        </div>
                        <div className="aeSmall">Blob: {r.id}</div>
                      </div>
                    </td>

                    <td>{cust}</td>
                    <td>
                      <div className="aeMain">
                        <div className="aeTitle">{prop}</div>
                        <div className="aeSmall">
                          {r.site?.siteAddress?.line1 || ""}
                        </div>
                      </div>
                    </td>

                    <td>{updated}</td>
                    <td>{total || "—"}</td>

                    <td>
                      <span className={statusClass(r.status)}>
                        {String(r.status || "draft")}
                      </span>
                    </td>

                    <td className="right">
                      <div className="aeRowActions">
                        <Link className="btn btnTiny" to={`/admin/estimates/edit/${r.id}`}>
                          Edit
                        </Link>

                        <a
                          className="btn btnTiny btnGhost"
                          href={`/estimate/${r.id}`}
                          target="_blank"
                          rel="noreferrer"
                        >
                          Public
                        </a>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

